/**
 * The NavLog on a phone: one card per leg instead of one row.
 *
 * A table with twenty columns on a 390 px screen is a table nobody reads, and a
 * horizontally scrolled one is a table nobody trusts — the number under your
 * thumb is in a column whose header has scrolled away. The card keeps the leg's
 * two ends in its heading, which is the only context every other value needs.
 *
 * Every cell is the same component the table uses, so what is editable here is
 * editable there and the derived legend is one implementation.
 */
import { useT } from '../../i18n';
import { cellFor, column, type Column } from './columns';
import { BLANK, CellComputed, CellInput, CellValue } from './NavLogCell';
import { formatFuel, legFuelL, feetToSafeAlt, safeAltToFeet, type NavRow } from './model';

/** The two the card's heading already carries. */
const IN_HEADING = new Set(['idx', 'cp']);

function CardValue({
  col, row, lph, read,
}: {
  col: Column;
  row: NavRow;
  lph: number | null;
  read(id: string): string;
}) {
  const t = useT();
  const spec = cellFor(col, row);
  if (spec.kind === 'none' || spec.kind === 'label') return <span className="nl-val">{BLANK}</span>;
  if (spec.kind === 'fuel') {
    return (
      <CellComputed
        value={formatFuel(legFuelL(lph, read(spec.time)))}
        title={t('th.fuel') + ' – ' + t('navlog.computed')}
      />
    );
  }
  if (spec.kind === 'pair') return <CellInput id={spec.to} />;
  if (!spec.editable) {
    return <CellValue id={spec.id} {...(col.editable !== true ? { computedColumn: true } : {})} />;
  }
  return (
    <CellInput
      id={spec.id}
      {...(col.key === 'safealt' ? { transform: { toInput: safeAltToFeet, fromInput: feetToSafeAlt } } : {})}
    />
  );
}

function Card({
  row, columns, lph, stale, read,
}: {
  row: NavRow;
  columns: readonly Column[];
  lph: number | null;
  stale: boolean;
  read(id: string): string;
}) {
  const t = useT();
  const heading =
    row.kind === 'leg' ? t('form.leg', { n: String(row.row) })
      : row.kind === 'alt' ? t('form.alt')
        : row.kind === 'dep' ? t('form.dep')
          : t('form.total');
  const pair = cellFor(column('cp'), row);

  return (
    <section className={'nl-card nl-r-' + row.kind}>
      <header className="nl-cardhead">
        <span className="ae-eyebrow">{heading}</span>
        {pair.kind === 'pair' ? (
          <span className="nl-pair">
            {pair.from ? <CellValue id={pair.from} className="nl-from" /> : null}
            {pair.from ? <span className="nl-arrow" aria-hidden="true">{'→'}</span> : null}
            <CellInput id={pair.to} />
          </span>
        ) : null}
        {stale ? <span className="ae-chip nl-stalechip">{t('winds.stale')}</span> : null}
      </header>
      <dl className="nl-dl">
        {columns.map(col => {
          if (IN_HEADING.has(col.key)) return null;
          const spec = cellFor(col, row);
          if (spec.kind === 'none') return null;
          return (
            <div className="nl-dd" key={col.key}>
              <dt className="ae-eyebrow" {...(col.full ? { title: t(col.full) } : {})}>
                {t(col.head)}
                {col.unit ? <span className="nl-unit">{col.unit}</span> : null}
              </dt>
              <dd className={col.text ? 'text' : 'num'}>
                <CardValue col={col} row={row} lph={lph} read={read} />
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

export function LegCards({
  rows, columns, lph, stale, read,
}: {
  rows: readonly NavRow[];
  columns: readonly Column[];
  lph: number | null;
  stale: ReadonlySet<number>;
  read(id: string): string;
}) {
  return (
    <div className="nl-cards">
      {rows.map(r => (
        <Card
          key={r.id}
          row={r}
          columns={columns}
          lph={lph}
          stale={r.kind === 'leg' && typeof r.row === 'number' && stale.has(r.row)}
          read={read}
        />
      ))}
    </div>
  );
}
